import { TrashIcon } from '@heroicons/react/24/outline'
import { Button, Card, Divider, Tooltip } from '@heroui/react'
import {
  getOptimalProfilePictureUrl,
  isProfilePictureProcessing,
  useProfilePictureManager,
} from '@qse/api'
import { ModelControlEnum, type User, UserRoleEnum } from '@qse/constants'
import { useActiveModelStore, useUserStore } from '@qse/stores'
import { DualProgressBar, PaymentOverdueBanner, UserAvatar } from '@qse/ui'
import { useIsMobile } from '@qse/utils'
import { truncateToDecimals } from '@qse/utils'
import { motion } from 'framer-motion'
import { useMemo, useRef, useState } from 'react'

import { APIKeyManagment } from '../components/API-key-managment'

const ProfileScreen: React.FC = () => {
  const { user, isLoading: isLoadingUser } = useUserStore()
  const isMobile = useIsMobile()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)

  // Use the profile picture manager hook
  // Note: Using same enabled condition as ProfileDropdown to ensure cache sharing and prevent duplicate API calls
  const { profilePicture, uploadProfilePicture, isUploading, deleteProfilePicture, isDeleting } =
    useProfilePictureManager({
      enabled:
        !!user &&
        user?.userRole !== UserRoleEnum.SUPER_ADMIN &&
        user?.userRole !== UserRoleEnum.RESELLER_ADMIN,
      tenantStatus: user?.tenant?.status,
    })
  const quotaSummary = useMemo(() => user?.drive?.quotaSummary, [user])

  // Extract values
  const used = Number(quotaSummary?.storage?.used) || 0
  const quota = Number(quotaSummary?.storage?.quota) || 0
  const pending = Number(quotaSummary?.storage?.pendingBytes) || 0
  const allocated = used + pending

  // Calculate percentages: if quota is 0 and used > 0, show 100% (over-quota)
  const usedPercent =
    quota === 0
      ? used > 0
        ? 100 // Show 100% when quota is 0 and used > 0
        : 0
      : quota && used
        ? truncateToDecimals((used / quota) * 100)
        : 0

  const allocatedPercent =
    quota === 0
      ? allocated > 0
        ? 100 // Show 100% when quota is 0 and allocated > 0
        : 0
      : quota && allocated
        ? truncateToDecimals((allocated / quota) * 100)
        : 0

  const freePercent =
    quota === 0
      ? used > 0
        ? 0 // No free space when over-quota
        : 100
      : quota && used
        ? truncateToDecimals(100 - (used / quota) * 100)
        : 100

  // Calculate storage data from user information - using same structure as sidebar
  const storageData = {
    used,
    total: quota,
    pending,
    allocated,
    usedPercent,
    allocatedPercent,
    freePercent,
  }

  // Calculate API quota data from user information - using same structure as sidebar
  const apiQuotaData = {
    used: Number(quotaSummary?.service?.used) || 0,
    total: Number(quotaSummary?.service?.quota) || 0,
    usedPercent:
      quotaSummary?.service?.quota && quotaSummary?.service?.used
        ? truncateToDecimals((quotaSummary.service.used / quotaSummary.service.quota) * 100)
        : 0,
  }

  const userType = user?.userRole || ''
  const isSuperAdmin =
    userType === UserRoleEnum.SUPER_ADMIN || userType === UserRoleEnum.RESELLER_ADMIN

  // Get the current profile picture URL - use large size for the main display
  const profilePictureUrl = isSuperAdmin
    ? null
    : getOptimalProfilePictureUrl(profilePicture, 'large')

  // Check if profile picture is still processing
  const profilePictureProcessing = profilePictureUrl
    ? false
    : isProfilePictureProcessing(profilePicture)

  // Helper function to get scroll behavior based on device type
  const getScrollBehavior = () => (isMobile ? 'inside' : 'outside') as 'inside' | 'outside'

  const handleUpdatePassword = () => {
    // Navigate to password update screen or open modal
    // For now, we'll just show an alert
    const config = {
      modalHeader: {
        title: 'Update Password',
        showCloseButton: true,
      },

      actions: [
        {
          label: 'Cancel',
          color: 'secondary',
        },
        {
          label: 'Move to Trash',
          color: 'primary',
          onPress: async () => {},
        },
      ],
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[504px]',
      modelActive: ModelControlEnum.UPDATE_PASSWORD,
      toggleModel: true,
      scrollBehavior: getScrollBehavior(),
    })
  }

  const handle2FAAction = async () => {
    if (user?.mfaStatus === 'enabled') {
      // 2FA is enabled, show disable modal
      useActiveModelStore.setState({
        data: {},
        width: 'max-w-[504px]',
        modelActive: ModelControlEnum.TWO_FA_DISABLE,
        toggleModel: true,
        scrollBehavior: getScrollBehavior(),
      })
    } else {
      // Always require setup if mfaStatus is 'disabled'
      if (user?.mfaStatus === 'disabled') {
        useActiveModelStore.setState({
          data: {},
          width: 'max-w-[504px]',
          modelActive: ModelControlEnum.TWO_FA_SETUP,
          toggleModel: true,
          scrollBehavior: getScrollBehavior(),
        })

        return
      }
    }
  }

  const handleEditProfileImage = () => {
    fileInputRef.current?.click()
  }

  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]

    if (!file) return

    // Create preview URL for immediate display
    const previewUrl = URL.createObjectURL(file)

    setPreviewImage(previewUrl)

    try {
      // Upload using the hook with progress tracking
      await uploadProfilePicture({
        file,
        onProgress: progress => {
          setUploadProgress(progress)
        },
      })

      // Update user store if response contains user data
      // Note: The hook already handles query invalidation and toast notifications

      // Clear preview since we now have the actual profile picture
      setPreviewImage(null)
      setUploadProgress(0)
    } catch {
      // Error handling is done in the hook
      // Clear preview on error
      if (previewImage) {
        URL.revokeObjectURL(previewImage)
      }
      setPreviewImage(null)
      setUploadProgress(0)
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDeleteProfileImage = () => {
    useActiveModelStore.setState({
      data: {
        title: 'Remove Profile Picture',
        message:
          'This will permanently delete your current profile picture and restore the default avatar. You can always upload a new picture later.',
        icon: '/assets/svg/warning.svg',
        actions: [
          {
            label: 'Cancel',
            color: 'default',
            variant: 'flat',
          },
          {
            label: 'Confirm',
            color: 'danger',
            variant: 'solid',
            onPress: async () => {
              try {
                // Use the hook to delete the profile picture
                await deleteProfilePicture()

                // Clear preview image if any
                if (previewImage) {
                  URL.revokeObjectURL(previewImage)
                  setPreviewImage(null)
                }

                // The hook handles query invalidation and toast notifications
              } catch {
                // Error handling is done in the hook
              }
            },
          },
        ],
      },
      width: 'max-w-[504px]',
      modelActive: ModelControlEnum.CONFIRMATION,
      toggleModel: true,
      scrollBehavior: getScrollBehavior(),
    })
  }

  if (isLoadingUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-background pb-20"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="h-[242px] dark:bg-[#0C131E] relative md:-top-28 top-0 bg-gray-100 p-[30px]">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Profile</h2>
      </div>
      <div className="-mt-24">
        <div className="flex  flex-col gap-[30px] justify-center items-center ">
          <div className="relative">
            {previewImage ? (
              <img
                alt="Profile preview"
                className="w-[185px] h-[185px] rounded-[20px] object-cover bg-gray-200 dark:bg-[#222] border-2 border-gray-300 dark:border-white/10"
                src={previewImage}
              />
            ) : profilePictureUrl ? (
              <img
                alt="Profile picture"
                className="w-[185px] h-[185px] rounded-[20px] object-cover bg-gray-200 dark:bg-[#222] border-2 border-gray-300 dark:border-white/10"
                src={profilePictureUrl}
              />
            ) : (
              <UserAvatar
                className="w-[185px] h-[185px] rounded-[20px] border-2 border-gray-300 dark:border-white/10"
                size="xl"
                user={user as unknown as User}
              />
            )}

            {/* Edit Profile Picture Button - Hidden for SUPER_ADMIN */}
            {!isSuperAdmin && (
              <Tooltip
                content={
                  isUploading || isDeleting || profilePictureProcessing
                    ? 'Please wait, processing...'
                    : 'Edit profile image'
                }
              >
                <button
                  className="absolute -top-2 -right-3 bg-gray-900 dark:bg-[#0C131E] border border-gray-200 dark:border-white rounded-full p-2 hover:bg-gray-800 dark:hover:bg-[#1a2332] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isUploading || isDeleting || profilePictureProcessing}
                  type="button"
                  onClick={handleEditProfileImage}
                >
                  <img alt="edit" className="w-4 h-4" src="/assets/svg/write.svg" />
                </button>
              </Tooltip>
            )}

            {/* Delete Profile Picture Button - show if user has a profile picture or preview and not SUPER_ADMIN */}
            {!isSuperAdmin && (profilePictureUrl || previewImage) && (
              <Tooltip
                content={
                  isUploading || isDeleting || profilePictureProcessing
                    ? 'Please wait, processing...'
                    : 'Delete profile image'
                }
              >
                <button
                  className="absolute -top-2 -left-3 bg-red-600 hover:bg-red-700 border border-red-500 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isUploading || isDeleting || profilePictureProcessing}
                  type="button"
                  onClick={handleDeleteProfileImage}
                >
                  <TrashIcon className="w-4 h-4 text-white" />
                </button>
              </Tooltip>
            )}

            {/* Hidden file input - Disabled for SUPER_ADMIN */}
            <input
              ref={fileInputRef}
              accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              disabled={isUploading || isDeleting || profilePictureProcessing || isSuperAdmin}
              type="file"
              onChange={handleProfileImageChange}
            />

            {/* Loading overlay */}
            {(isUploading || isDeleting || profilePictureProcessing) && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-[20px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto" />
                  <p className="text-white text-sm mt-2">
                    {isUploading
                      ? `Uploading... ${uploadProgress}%`
                      : isDeleting
                        ? 'Deleting...'
                        : profilePictureProcessing
                          ? 'Processing...'
                          : ''}
                  </p>
                </div>
              </div>
            )}

            {/* Preview indicator */}
            {previewImage && !isUploading && (
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                Preview
              </div>
            )}
          </div>
          <div className="flex gap-1 flex-col text-center">
            <p className="text-2xl font-medium">
              {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'}
            </p>
            <p className="text-sm">{user?.email || 'user@example.com'}</p>
          </div>
        </div>
        <div className="mt-8 flex justify-center items-center">
          <div className="w-[651px] flex flex-col gap-4 md:px-0 px-4">
            <PaymentOverdueBanner isInView={false} />
            {user?.userRole !== UserRoleEnum.SUPER_ADMIN && (
              <div className="px-3 py-5 dark:bg-[#FFFFFF1A] bg-gray-100 rounded-2xl flex flex-col gap-4">
                <p className=" font-medium text-[28px]">Storage Details</p>

                <Card className="shadow-none bg-transparent rounded-none border-none transition-all duration-200">
                  <div className="py-0">
                    <div className="w-full">
                      {/* Storage Title with Status */}
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-900 dark:text-white font-medium text-base">
                          <span className="font-semibold">Storage</span>
                          <span className="font-normal">
                            {' '}
                            {storageData.total === 0 && storageData.used > 0
                              ? '(Over quota)'
                              : `(${storageData.allocatedPercent}% full)`}
                          </span>
                        </span>
                      </div>

                      {/* Dual Progress Bar */}
                      <Tooltip
                        content={
                          <div className="max-w-[280px] whitespace-normal">
                            {storageData.total === 0 && storageData.used > 0
                              ? 'You have exceeded your storage quota due to your plan change. Delete files to restore upload and copy operations.'
                              : storageData.used === storageData.total
                                ? 'Your storage is full. Delete files to free up space and continue uploading.'
                                : 'You have exceeded your storage quota due to your plan change. Delete files to restore upload and copy operations.'}
                          </div>
                        }
                        isDisabled={
                          !(storageData.total === 0
                            ? storageData.used > 0
                            : storageData.used >= storageData.total)
                        }
                      >
                        <div>
                          <DualProgressBar
                            allocated={storageData.pending}
                            className="mb-3"
                            height="h-3"
                            pendingBytes={storageData.pending}
                            showValueDisplay={true}
                            total={storageData.total}
                            used={storageData.used}
                            usedPercentage={storageData.usedPercent}
                            isOverQuota={
                              storageData.total === 0
                                ? storageData.used > 0
                                : storageData.used >= storageData.total
                            }
                          />
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                </Card>
              </div>
            )}
            {user?.userRole !== UserRoleEnum.SUPER_ADMIN && (
              <div className="px-3 py-5 dark:bg-[#FFFFFF1A] bg-gray-100 rounded-2xl flex flex-col gap-4">
                <p className="font-medium text-[28px]">API Quota Details</p>

                <Card className="shadow-none bg-transparent rounded-none border-none transition-all duration-200">
                  <div className="py-0">
                    <div className="w-full">
                      {/* API Quota Title with Status */}
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-900 dark:text-white font-medium text-base">
                          <span className="font-semibold">API Quota</span>
                          <span className="font-normal"> ({apiQuotaData.usedPercent}% used)</span>
                        </span>
                      </div>

                      {/* Dual Progress Bar for API Quota */}
                      <Tooltip
                        isDisabled={!(apiQuotaData.used >= apiQuotaData.total)}
                        content={
                          <div className="max-w-[280px] whitespace-normal">
                            {apiQuotaData.used === apiQuotaData.total
                              ? 'Your API quota is full. Your usage will reset on the next cycle.'
                              : 'You have exceeded your API quota. Your usage will reset on the next cycle.'}
                          </div>
                        }
                      >
                        <div>
                          <DualProgressBar
                            allocatedColor="bg-green-200 dark:bg-green-300/30"
                            className="mb-3"
                            formatTotalValue={value => `${value.toLocaleString()} calls`}
                            formatUsedValue={value => `${value.toLocaleString()} calls`}
                            height="h-3"
                            isOverQuota={apiQuotaData.used >= apiQuotaData.total}
                            showValueDisplay={false}
                            total={apiQuotaData.total}
                            trackColor="bg-gray-200 dark:bg-gray-700"
                            unit="calls"
                            used={apiQuotaData.used}
                            usedColor="bg-green-600 dark:bg-green-400"
                            usedPercentage={apiQuotaData.usedPercent}
                          />
                        </div>
                      </Tooltip>

                      {/* API Quota Details */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-white/60">
                          {apiQuotaData.used.toLocaleString()} /{' '}
                          {apiQuotaData.total.toLocaleString()} calls
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            )}
            <div className="px-3 py-5 dark:bg-[#FFFFFF1A] bg-gray-100 rounded-2xl flex flex-col gap-4">
              <p className=" font-medium text-[28px]">Security</p>

              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex flex-col gap-1">
                  <p className="font-medium text-[22px]">Update Password</p>
                  <p className="text-sm">If you change your password, you will be logged out.</p>
                </div>
                <Button
                  color="primary"
                  radius="sm"
                  size="md"
                  variant="bordered"
                  onPress={handleUpdatePassword}
                >
                  Update Password
                </Button>
              </div>
              {!isSuperAdmin ? (
                <>
                  <Divider className="bg-slate-300 dark:bg-slate-500" />
                  <div className="flex justify-between items-center border-t border-divider pt-4 flex-wrap gap-4">
                    <div className="flex flex-col gap-1">
                      <p className="font-medium text-[22px]">Two Factor Authentication</p>
                      <p className="text-sm">
                        Protect your account with a second layer of security.
                      </p>
                    </div>
                    <Button color="primary" radius="sm" size="md" onPress={handle2FAAction}>
                      {user?.mfaStatus === 'enabled'
                        ? 'Disable Two-Factor Authentication'
                        : 'Enable Two-Factor Authentication'}
                    </Button>
                  </div>

                  <Divider className="bg-slate-300 dark:bg-slate-500" />
                  <APIKeyManagment />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default ProfileScreen
